/**
 * Plays the music files in `/music` (04 §5; trackNames.ts for the naming). The synthesised
 * score is the fallback wherever there is no file, so this never has to succeed: `play` says
 * whether it did. Audio elements, so one track fades out under the next and no AudioContext is
 * needed. Ported from STONEFALL.
 */

import { trackTable } from './trackNames';

// Found at build time; an empty folder gives an empty table and every track is synthesised.
const FILES = import.meta.glob('/music/*.{mp3,ogg,m4a,wav,flac}', { query: '?url', import: 'default', eager: true });

const FADE_SECONDS = 1.2;
/** A jingle is over in seconds: what was playing gets out of its way at once. */
const QUICK_SECONDS = 0.15;
const STEP_MS = 50;

/** Every music file found, by track name (for the console, and for a page with no WebAudio). */
export function musicFileNames(): string[] {
  return [...trackTable(FILES as Record<string, string>).keys()].sort();
}

export class FileMusic {
  private readonly table = trackTable(FILES as Record<string, string>);
  private current: HTMLAudioElement | null = null;
  private currentName: string | null = null;
  /** Tracks on their way out, and how much volume each loses a step. */
  private readonly fading = new Map<HTMLAudioElement, number>();
  private volume = 0.5;
  private timer: ReturnType<typeof setInterval> | null = null;
  /** A browser refuses to start audio before the first key or click: remembered and started then. */
  private blocked = false;

  names(): string[] {
    return [...this.table.keys()].sort();
  }

  /** Whether there is a file for the track called `name`. */
  has(name: string): boolean {
    return this.table.has(name);
  }

  /**
   * Fade to the file called `name`. False, and nothing changes, if there is none (the caller
   * then stops the files and plays the synth). `once`: a short piece that plays through and
   * stops rather than looping, and that the track before it makes way for at once.
   */
  play(name: string, once = false): boolean {
    const url = this.table.get(name);
    if (url === undefined) return false;
    if (name === this.currentName) return true;
    this.retire(once);
    const el = new Audio(url);
    el.loop = !once;
    // A piece that has played out is no longer playing: the next request for it starts it again.
    el.addEventListener('ended', () => {
      if (el !== this.current) return;
      this.current = null;
      this.currentName = null;
    });
    // A jingle starts at full volume: a fade-in would swallow its first notes.
    el.volume = once ? this.volume : 0;
    this.current = el;
    this.currentName = name;
    this.start(el);
    this.run();
    return true;
  }

  /** `quick`: something short is about to play in its place. */
  stop(quick = false): void {
    this.retire(quick);
    this.run();
  }

  /** 0..1, already including the master slider. */
  setVolume(volume: number): void {
    this.volume = Math.min(1, Math.max(0, volume));
    this.run();
  }

  /** Call from any key or click: starts what the browser would not let start earlier. */
  unlock(): void {
    if (this.blocked && this.current) this.start(this.current);
  }

  private start(el: HTMLAudioElement): void {
    this.blocked = false;
    el.play().catch(() => {
      if (el === this.current) this.blocked = true;
    });
  }

  private retire(quick: boolean): void {
    if (this.current) this.fading.set(this.current, STEP_MS / 1000 / (quick ? QUICK_SECONDS : FADE_SECONDS));
    this.current = null;
    this.currentName = null;
  }

  /** One fade loop for everything: the current track toward the volume, retired ones toward silence. */
  private run(): void {
    if (this.timer !== null) return;
    const step = STEP_MS / 1000 / FADE_SECONDS;
    this.timer = setInterval(() => {
      let busy = false;
      const el = this.current;
      if (el && Math.abs(el.volume - this.volume) > 0.001) {
        el.volume = el.volume < this.volume
          ? Math.min(this.volume, el.volume + step)
          : Math.max(this.volume, el.volume - step);
        busy = true;
      }
      for (const [old, loss] of this.fading) {
        old.volume = Math.max(0, old.volume - loss);
        if (old.volume <= 0.001) {
          old.pause();
          old.removeAttribute('src');
          this.fading.delete(old);
        } else busy = true;
      }
      if (!busy && this.timer !== null) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }, STEP_MS);
  }
}
